import { MimeType } from "@/type/core/api/api.types";
import { ROUTES } from "@routes/Routes";
import {
  CALLBACK_URL_PARAM,
  DEFAULT_500_ERROR,
  WS_TIMEOUT,
  WS_UPLOAD_TIMEOUT,
} from "@utils/Constant";
import { toastError } from "@utils/helpers/toast-helper";
import logger from "@utils/logger";
import {
  ParsedResponse,
  ServerResponseParser,
} from "@utils/rest/ServerResponseParse";
import "abortcontroller-polyfill";
import { SorterResult } from "antd/es/table/interface";
import { t } from "i18next";
import "promise-polyfill/src/polyfill";
import "unfetch/polyfill";

type RestMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface DataWithId {
  id: string;
}

interface SearchDto {
  [index: string]: any;
}

const executeCall = <U>(fetchPromise: Promise<Response>) => {
  return fetchPromise
    .then((response: Response) => {
      logger.debug(`HTTP response ${response.status}`);
      if (response.status === 401) {
        location.href = `${ROUTES.auth.login.generate()}?${CALLBACK_URL_PARAM}=${
          location.pathname
        }`;
      }
      return new ServerResponseParser<U>().parseResponse(response);
    })
    .catch(() => {
      return DEFAULT_500_ERROR;
    });
};

const removeTrailingSlash = (url: string): string => {
  return url.endsWith("/") ? url.substring(0, url.length - 1) : url;
};

const cleanUrl = (url: string): string => {
  const items = url.split("?");
  if (items.length === 1) {
    return removeTrailingSlash(items[0]);
  }

  const u = items[0];
  const q = items[1];

  return `${removeTrailingSlash(u)}?${q}`;
};

function getCookie(cname: string): string {
  const name = cname + "=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

export const getToken = (): string => {
  // console.log(localStorage.getItem("persist:root"))
  // JSON.parse(JSON.parse(localStorage.getItem("persist:root")).auth2)
  return getCookie("ECMA-TOKEN");
  // JSON.parse(JSON.parse(localStorage.getItem("persist:root")).auth2).auth2Token;
};

const AUTH_TYPE = "bearer";

const makeRestCall = <T, U>(
  url: string,
  method: RestMethod,
  data?: T,
  specificTimeout: number = WS_TIMEOUT,
  contentType: MimeType = "application/json",
  accept: MimeType = "application/json",
): Promise<ParsedResponse<U>> => {
  const controller = new AbortController();
  const signal = controller.signal;
  const cleanedUrl = cleanUrl(url);
  const timeout = setTimeout(() => {
    toastError(t("error.toast.serverTimeout"));
    controller.abort();
  }, specificTimeout);

  const headers: HeadersInit = {
    ["Content-Type"]: contentType,
    Accept: accept,
    Authorization: AUTH_TYPE + " " + getToken(),
  };

  const fetchPromise = fetch(cleanedUrl, {
    method,
    signal,
    credentials: "include",
    body: data ? JSON.stringify(data) : undefined,
    headers,
  });
  return executeCall<U>(fetchPromise).then((response) => {
    clearTimeout(timeout);
    return response;
  });
};

const makeRestMultipartCall = <U>(
  url: string,
  method: RestMethod,
  data: FormData,
): Promise<ParsedResponse<U>> => {
  const controller = new AbortController();
  const signal = controller.signal;
  const cleanedUrl = cleanUrl(url);

  const headers: HeadersInit = {
    Authorization: AUTH_TYPE + " " + getToken(),
  };

  const timeout = setTimeout(() => {
    toastError(t("error.toast.serverTimeout"));
    controller.abort();
  }, WS_UPLOAD_TIMEOUT);
  const fetchPromise = fetch(cleanedUrl, {
    method,
    signal,
    credentials: "include",
    body: data,
    headers,
  });
  return executeCall<U>(fetchPromise).then((response) => {
    clearTimeout(timeout);
    return response;
  });
};

const computeQueryParamPrefix = (index: number): string => {
  return index !== 0 ? "&" : "";
};

const buildSearchParameters = <T extends SearchDto>(dto?: T): string => {
  return dto
    ? Object.keys(dto)
        .map((key, index) =>
          dto[key] !== undefined
            ? `${computeQueryParamPrefix(index)}${key}=${String(dto[key])}`
            : "",
        )
        .join("")
    : "";
};

export const restListHandlerPagination = <T, U extends SearchDto = {}>(
  endpointUrl: string,
): ((data: { dto?: U }) => Promise<ParsedResponse<T>>) => {
  return ({ dto }: { dto?: U }) => {
    return makeRestCall<U, T>(`${endpointUrl}`, "POST", dto);
  };
};

export const restListHandler = <T, U extends SearchDto = {}>(
  endpointUrl: string,
): ((data: {
  sorter?: SorterResult<any>;
  dto?: U;
}) => Promise<ParsedResponse<T[]>>) => {
  return ({ sorter, dto }: { sorter?: SorterResult<any>; dto?: U }) => {
    const searchParameters = buildSearchParameters<U>(dto);
    let order;
    switch (sorter?.order) {
      case "ascend":
        order = ",ASC";
        break;
      case "descend":
        order = ",DESC";
        break;
      default:
        order = "";
        break;
    }
    return makeRestCall<void, T[]>(
      `${endpointUrl}?sort=${
        (order && sorter?.columnKey) || ""
      }${order}&${searchParameters}`,
      "GET",
    );
  };
};

export const restGetHandler = <U, T extends SearchDto = {}>(
  endpointUrl: string,
  specificTimeout?: number,
  contentType?: MimeType,
  accept?: MimeType,
): ((data: { dto?: T }) => Promise<ParsedResponse<U>>) => {
  return ({ dto }: { dto?: T }) => {
    const searchParameters = buildSearchParameters<T>(dto);
    return makeRestCall<void, U>(
      `${endpointUrl}?${searchParameters}`,
      "GET",
      undefined,
      specificTimeout,
      contentType,
      accept,
    );
  };
};

export const restDetailsHandler = <U, T extends SearchDto = {}>(
  endpointUrl: string,
  suffixUrl?: string,
  specificTimeout?: number,
  contentType?: MimeType,
  accept?: MimeType,
): ((data: {
  dto?: T;
  id: string;
  upperEntityId?: string;
  token?: string;
}) => Promise<ParsedResponse<U>>) => {
  return (data: {
    dto?: T;
    id: string;
    upperEntityId?: string;
    token?: string;
  }) => {
    const searchParameters = buildSearchParameters<T>(data.dto);

    return makeRestCall<void, U>(
      endpointUrl +
        (data.upperEntityId ?? "") +
        (data.id ? `/${data.id}` : "") +
        (suffixUrl ?? "") +
        (data.token ? `?token=${data.token}` : "") +
        `${data.token ? "&" : data.dto ? "?" : ""}${searchParameters}`,
      "GET",
      undefined,
      specificTimeout,
      contentType,
      accept,
    );
  };
};

export const restGetUniqueHandler = <T, U extends SearchDto = {}>(
  endpointUrl: string,
  suffixUrl?: string,
): ((data: { dto?: U }) => Promise<ParsedResponse<T>>) => {
  return ({ dto }: { dto?: U }) => {
    const searchParameters = buildSearchParameters<U>(dto);
    return makeRestCall<void, T>(
      `${endpointUrl + (suffixUrl ? suffixUrl : "")}?${searchParameters}`,
      "GET",
    );
  };
};

export const restIdListHandler = <T>(
  endpointUrl: string,
): ((id: string) => Promise<ParsedResponse<T[]>>) => {
  return (id: string) => makeRestCall<void, T[]>(endpointUrl + id, "GET");
};

export const restCreationHandler = <T, U>(
  endpointUrl: string,
  suffixUrl?: string,
  suffix2Url?: string,
): ((data: {
  upperEntityId?: string;
  upperEntity2Id?: string;
  dto: T;
}) => Promise<ParsedResponse<U>>) => {
  return (data: { upperEntityId?: string; upperEntity2Id?: string; dto: T }) =>
    makeRestCall<T, U>(
      endpointUrl +
        (data.upperEntityId ? data.upperEntityId : "") +
        (suffixUrl ? suffixUrl : "") +
        (data.upperEntity2Id ? data.upperEntity2Id : "") +
        (suffix2Url ? suffix2Url : ""),
      "POST",
      data.dto,
    );
};

export const restCreationMultipartHandler = <U>(
  endpointUrl: string,
  suffixUrl?: string,
): ((data: { dto: FormData }) => Promise<ParsedResponse<U>>) => {
  return (data: { upperEntityId?: string; dto: FormData }) =>
    makeRestMultipartCall<U>(
      endpointUrl + (suffixUrl ? suffixUrl : ""),
      "POST",
      data.dto,
    );
};

export const restUpdateMultipartHandler = <U>(
  endpointUrl: string,
  suffixUrl?: string,
): ((data: { id?: string; dto: FormData }) => Promise<ParsedResponse<U>>) => {
  return (data: { id?: string; dto: FormData }) =>
    makeRestMultipartCall<U>(
      endpointUrl + (data.id ? `/${data.id}` : "") + (suffixUrl ?? ""),
      "PUT",
      data.dto,
    );
};

export const restDeletionHandler = <T>(
  endpointUrl: string,
  suffixUrl?: string,
): ((data: {
  upperEntityId?: string;
  id?: string;
  dto?: T;
}) => Promise<ParsedResponse<void>>) => {
  return (data: { upperEntityId?: string; id?: string; dto?: T }) =>
    makeRestCall<T, void>(
      endpointUrl +
        (data.upperEntityId ?? "") +
        (data.id ? `/${data.id}` : "") +
        (suffixUrl ?? ""),
      "DELETE",
      data.dto,
    );
};

export const restUpdateHandler = <T, U>(
  endpointUrl: string,
  suffixUrl?: string,
): ((data: {
  upperEntityId?: string;
  id?: string;
  dto: T;
}) => Promise<ParsedResponse<U>>) => {
  return (data: { upperEntityId?: string; id?: string; dto: T }) =>
    makeRestCall<T, U>(
      endpointUrl +
        (data.upperEntityId ? data.upperEntityId : "") +
        (data.id ? `/${data.id}` : "") +
        (suffixUrl ? suffixUrl : ""),
      "PUT",
      data.dto,
    );
};

export const restUpdateHandlerWithParams = <T, U>(
  endpointUrl: string,
  suffixUrl?: string,
): ((data: {
  upperEntityId?: string;
  id?: string;
  value?: string;
  dto: T;
}) => Promise<ParsedResponse<U>>) => {
  return (data: {
    upperEntityId?: string;
    id?: string;
    value?: string;
    dto: T;
  }) =>
    makeRestCall<T, U>(
      endpointUrl +
        (data.upperEntityId ? data.upperEntityId : "") +
        (data.id ? `/${data.id}` : "") +
        (suffixUrl ? suffixUrl : "") +
        (data.value ? `${data.value}` : ""),
      "PUT",
      data.dto,
    );
};

export const restPatchHandler = <T, U>(
  endpointUrl: string,
  suffixUrl?: string,
): ((data: {
  upperEntityId?: string;
  id: string;
  dto: T;
}) => Promise<ParsedResponse<U>>) => {
  return (data: { upperEntityId?: string; id: string; dto: T }) =>
    makeRestCall<T, U>(
      endpointUrl +
        (data.upperEntityId ? data.upperEntityId : data.id) +
        (suffixUrl ? suffixUrl : "") +
        (data.upperEntityId ? data.id : ""),
      "PATCH",
      data.dto,
    );
};

export const restPostHandler = <T, U>(
  endpointUrl: string,
  suffixUrl?: string,
  specificTimeout?: number,
): ((data: {
  upperEntityId?: string;
  dto?: T;
  id?: string;
}) => Promise<ParsedResponse<U>>) => {
  return (data: { upperEntityId?: string; dto?: T; id?: string }) =>
    makeRestCall<T, U>(
      endpointUrl +
        (data.upperEntityId ? data.upperEntityId : "") +
        (data.id ? `/${data.id}` : "") +
        (suffixUrl ? suffixUrl : ""),
      "POST",
      data.dto,
      specificTimeout,
    );
};

export const restPostHandlerWithMultipleIds = <T, U>(
  endpointUrl: string,
  midUrl?: string,
  suffixUrl?: string,
): ((data: {
  dto?: T;
  midId?: string;
  id?: string;
}) => Promise<ParsedResponse<U>>) => {
  return (data: { dto?: T; midId?: string; id?: string }) =>
    makeRestCall<T, U>(
      `${endpointUrl}${data.midId ? `/${data.midId}` : ""}${midUrl ?? ""}${data.id ? `/${data.id}` : ""}${suffixUrl ?? ""}`,
      "POST",
      data.dto,
    );
};

export const restPostHandlerReceivingMultipart = <T, U>(
  endpointUrl: string,
  suffixUrl?: string,
): ((data: {
  upperEntityId?: string;
  dto?: T;
}) => Promise<ParsedResponse<U>>) => {
  return (data: { upperEntityId?: string; dto?: T }) =>
    makeRestCall<T, U>(
      endpointUrl +
        (data.upperEntityId ? data.upperEntityId : "") +
        (suffixUrl ? suffixUrl : ""),
      "POST",
      data.dto,
      WS_UPLOAD_TIMEOUT,
      "application/json",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );
};

export const restWorkflowHandler = <T, U>(
  endpointUrl: string,
  suffixUrl?: string,
): ((data: {
  upperEntityId?: string;
  id: string;
  dto: T;
}) => Promise<ParsedResponse<U>>) => {
  return (data: { upperEntityId?: string; id: string; dto: T }) =>
    makeRestCall<T, U>(
      endpointUrl +
        (data.upperEntityId ? data.upperEntityId : "") +
        (suffixUrl ? suffixUrl : "") +
        data.id +
        "/workflow",
      "POST",
      data.dto,
    );
};

export const restFieldUpdateHandler = <T, U>(
  endpointUrl: string,
  suffixUrl?: string,
): ((data: { id: string; dto: T }) => Promise<ParsedResponse<U>>) => {
  return (data: { id: string; dto: T }) =>
    makeRestCall<T, U>(
      endpointUrl + data.id + (suffixUrl ? suffixUrl : ""),
      "PATCH",
      data.dto,
    );
};

export const restIncreaseOrderHandler = <T extends DataWithId>(
  endpointUrl: string,
): ((data: T) => Promise<ParsedResponse<void>>) => {
  return (data: T) =>
    makeRestCall<T, void>(endpointUrl + data.id + "/increaseOrder", "POST");
};

export const restDecreaseOrderHandler = <T extends DataWithId>(
  endpointUrl: string,
): ((data: T) => Promise<ParsedResponse<void>>) => {
  return (data: T) =>
    makeRestCall<T, void>(endpointUrl + data.id + "/decreaseOrder", "POST");
};
